import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AppUpdateService {
    private swUpdate = inject(SwUpdate);
    private appRef = inject(ApplicationRef);

    constructor() {
        // No hay limpieza de Service Worker al arrancar: borrar las cachés «ngsw:*»
        // vaciaba la base de control del propio worker (ngsw:/:db:control), así que cada
        // arranque dejaba el service worker sin cachés y sin capacidad de servir offline.
        if (this.swUpdate.isEnabled) {
            // Verificar actualizaciones cada 30 segundos
            const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
            const every30Seconds$ = interval(30 * 1000);
            const every30SecondsOnceAppIsStable$ = concat(appIsStable$, every30Seconds$);

            every30SecondsOnceAppIsStable$.subscribe(async () => {
                try {
                    const updateFound = await this.swUpdate.checkForUpdate();
                    console.log(updateFound ? 'Nueva versión disponible' : 'Ya estás en la última versión');
                } catch (err) {
                    console.error('Error al verificar actualizaciones:', err);
                }
            });

            // Escuchar cuando una nueva versión está lista
            this.swUpdate.versionUpdates.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')).subscribe((event) => {
                console.log('Nueva versión detectada:', event.latestVersion);

                // Recargar automáticamente la página para aplicar la actualización
                if (confirm('Nueva versión disponible. ¿Recargar la aplicación?')) {
                    window.location.reload();
                }
            });

            // Manejar activación de versiones no manejadas
            this.swUpdate.unrecoverable.subscribe((event) => {
                console.error('Service Worker en estado irrecuperable:', event.reason);
                if (confirm('La aplicación está en un estado inconsistente. ' + '¿Deseas recargar la aplicación?')) {
                    window.location.reload();
                }
            });
        }
    }

    /**
     * Forzar verificación de actualizaciones manualmente
     */
    async checkForUpdates(): Promise<boolean> {
        if (!this.swUpdate.isEnabled) {
            return false;
        }

        try {
            return await this.swUpdate.checkForUpdate();
        } catch (err) {
            console.error('Error al verificar actualizaciones:', err);
            return false;
        }
    }

    /**
     * Activar inmediatamente una actualización pendiente
     */
    async activateUpdate(): Promise<boolean> {
        if (!this.swUpdate.isEnabled) {
            return false;
        }

        try {
            const activated = await this.swUpdate.activateUpdate();
            if (activated) {
                window.location.reload();
            }
            return activated;
        } catch (err) {
            console.error('Error al activar actualización:', err);
            return false;
        }
    }

    /**
     * Desregistrar completamente todos los Service Workers (solo para depuración)
     */
    async unregisterAllServiceWorkers(): Promise<void> {
        if (!('serviceWorker' in navigator)) {
            return;
        }

        try {
            const registrations = await navigator.serviceWorker.getRegistrations();

            for (const registration of registrations) {
                console.log('Desregistrando Service Worker:', registration.scope);
                await registration.unregister();
            }

            // Limpiar todas las cachés
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                for (const cacheName of cacheNames) {
                    console.log('Eliminando caché:', cacheName);
                    await caches.delete(cacheName);
                }
            }

            console.log('Todos los Service Workers y cachés han sido eliminados');
            window.location.reload();
        } catch (err) {
            console.error('Error al desregistrar Service Workers:', err);
        }
    }
}
